/** 七种折法的数据定义。
 *
 * 每个模式的层数、扇区角、折线角、纸张形状、尺寸语义、辅助线要求
 * 均在此单点声明；UI、预览、教程、PDF 只读取，不重算。
 * 几何推导见 docs/fold-geometry.md。
 */
import type { FoldId, SizeKind } from '../types';

export type PaperShape = 'a4' | 'square';

export interface FoldStep {
  /** 步骤编号，与打印页上的折叠编号一致。 */
  index: number;
  /** 简短中文说明。 */
  text: string;
  /** 该步折线的角度（度，过中心的直线方向）；null 表示无需折线的辅助步。 */
  creaseAngleDeg: number | null;
  /** 山折 / 谷折 / 辅助线。 */
  kind: 'mountain' | 'valley' | 'guide';
  /** 该步示意用的二维提示。 */
  hint: string;
}

export interface FoldDefinition {
  id: FoldId;
  /** 界面显示名，必须与规格书表格一致。 */
  name: string;
  /** 目标叠纸层数。 */
  layers: number;
  /** 扇区角（度）= 360 / layers。 */
  sectorAngleDeg: number;
  /** 第一条折线的方向角（度）。 */
  baseAngleDeg: number;
  /** 无向折线条数 = layers / 2。 */
  creaseCount: number;
  paper: PaperShape;
  /** 成品尺寸语义，不同模式不得混用而不解释。 */
  sizeKind: SizeKind;
  /** 建议纸厚（g/m²）。 */
  recommendedGsm: string;
  /** 最小连接宽度建议（mm）。 */
  minBridgeMm: number;
  /** 最小可剪面积建议（mm²）。 */
  minHoleAreaMm2: number;
  difficulty: 1 | 2 | 3 | 4 | 5;
  /** 是否需要角度辅助线 / 预裁。 */
  needsAngleGuide: boolean;
  /** 取纸与预裁说明。 */
  paperNote: string;
  /** 折叠步骤。 */
  steps: FoldStep[];
  /** 适合内容的简短描述。 */
  goodFor: string;
}

const step = (
  index: number,
  text: string,
  creaseAngleDeg: number | null,
  kind: FoldStep['kind'],
  hint: string,
): FoldStep => ({ index, text, creaseAngleDeg, kind, hint });

export const FOLDS: Record<FoldId, FoldDefinition> = {
  half: {
    id: 'half',
    name: '对折',
    layers: 2,
    sectorAngleDeg: 180,
    baseAngleDeg: 90,
    creaseCount: 1,
    paper: 'a4',
    sizeKind: 'widthHeight',
    recommendedGsm: '80–120',
    minBridgeMm: 2,
    minHoleAreaMm2: 6,
    difficulty: 1,
    needsAngleGuide: false,
    paperNote: '直接使用整张 A4，不裁切。',
    goodFor: '爱心、蝴蝶、动物剪影',
    steps: [
      step(1, '把 A4 纸沿长边中线对折，两短边对齐。', 90, 'valley', '折线竖直，平分纸宽。'),
      step(2, '折好后可编辑的是左侧半张，剪口对称到右侧半张。', null, 'guide', '折痕朝左，开口朝右。'),
    ],
  },
  quarter: {
    id: 'quarter',
    name: '四等分折',
    layers: 4,
    sectorAngleDeg: 90,
    baseAngleDeg: 90,
    creaseCount: 2,
    paper: 'a4',
    sizeKind: 'widthHeight',
    recommendedGsm: '80–120',
    minBridgeMm: 2,
    minHoleAreaMm2: 6,
    difficulty: 1,
    needsAngleGuide: false,
    paperNote: '直接使用整张 A4，不裁切。',
    goodFor: '四向对称图案',
    steps: [
      step(1, '沿长边中线对折。', 90, 'valley', '折线竖直。'),
      step(2, '再沿短边中线对折，得到四层。', 0, 'valley', '折线水平，与上一步垂直。'),
      step(3, '可编辑区域为左上四分之一张。', null, 'guide', '尖角在左下。'),
    ],
  },
  radial45: {
    id: 'radial45',
    name: '四角放射折',
    layers: 8,
    sectorAngleDeg: 45,
    baseAngleDeg: 0,
    creaseCount: 4,
    paper: 'square',
    sizeKind: 'diameter',
    recommendedGsm: '80–100',
    minBridgeMm: 2.5,
    minHoleAreaMm2: 8,
    difficulty: 2,
    needsAngleGuide: false,
    paperNote: '先把 A4 裁成正方形（最大 190 × 190 mm），再折叠。',
    goodFor: '四角窗花、四叶草',
    steps: [
      step(1, '把正方形纸沿一条对角线对折。', 45, 'valley', '三角形，顶点朝下。'),
      step(2, '再沿另一条对角线对折，四层。', 135, 'valley', '仍需对齐顶点。'),
      step(3, '沿中线对折一次，得到 45° 楔形，共八层。', 0, 'valley', '楔形两直角边等长。'),
    ],
  },
  radial36: {
    id: 'radial36',
    name: '五角放射折',
    layers: 10,
    sectorAngleDeg: 36,
    baseAngleDeg: 0,
    creaseCount: 5,
    paper: 'square',
    sizeKind: 'diameter',
    recommendedGsm: '70–90',
    minBridgeMm: 3,
    minHoleAreaMm2: 10,
    difficulty: 4,
    needsAngleGuide: true,
    paperNote: '先裁正方形；再按打印出的 36° 定位线收成楔形。正方形靠对折得不到 36°，必须用辅助线。',
    goodFor: '五角星、五瓣花',
    steps: [
      step(1, '把正方形裁好，印好 36° 辅助线的面朝上。', null, 'guide', '辅助线从中心放射。'),
      step(2, '沿第一条辅助线把纸收拢，形成 36° 楔。', 0, 'valley', '边缘对齐辅助线。'),
      step(3, '沿楔形中线对折，得到十层。', 18, 'valley', '楔形变窄一半。'),
    ],
  },
  radial30: {
    id: 'radial30',
    name: '六角放射折',
    layers: 12,
    sectorAngleDeg: 30,
    baseAngleDeg: 0,
    creaseCount: 6,
    paper: 'square',
    sizeKind: 'diameter',
    recommendedGsm: '60–80',
    minBridgeMm: 3,
    minHoleAreaMm2: 10,
    difficulty: 4,
    needsAngleGuide: true,
    paperNote: '先裁正方形；再按 30° 定位线折叠。60° 无法由对折得到，必须使用辅助线。',
    goodFor: '雪花、六瓣花',
    steps: [
      step(1, '裁好正方形，辅助线面朝上。', null, 'guide', '30° 定位线共 12 条。'),
      step(2, '沿定位线把纸分成三等分并收拢。', 0, 'valley', '先得 60°，再对折成 30°。'),
      step(3, '最后对折一次，得到十二层。', 15, 'valley', '注意保持中心对齐。'),
    ],
  },
  radial22_5: {
    id: 'radial22_5',
    name: '八角放射折',
    layers: 16,
    sectorAngleDeg: 22.5,
    baseAngleDeg: 0,
    creaseCount: 8,
    paper: 'square',
    sizeKind: 'diameter',
    recommendedGsm: '50–70',
    minBridgeMm: 4,
    minHoleAreaMm2: 12,
    difficulty: 4,
    needsAngleGuide: false,
    paperNote: '先按四角放射折折到 45° 楔，再沿角平分线对折一次；16 层已明显变厚。',
    goodFor: '八瓣窗花、装饰花边',
    steps: [
      step(1, '按四角放射折折到 45° 楔（八层）。', 0, 'valley', '同四角放射折第 3 步。'),
      step(2, '把 45° 楔沿角平分线再对折。', 22.5, 'valley', '得到 22.5° 楔，十六层。'),
    ],
  },
  radial15: {
    id: 'radial15',
    name: '十二角放射折',
    layers: 24,
    sectorAngleDeg: 15,
    baseAngleDeg: 0,
    creaseCount: 12,
    paper: 'square',
    sizeKind: 'diameter',
    recommendedGsm: '40–60',
    minBridgeMm: 5,
    minHoleAreaMm2: 16,
    difficulty: 5,
    needsAngleGuide: true,
    paperNote: '先按六角放射折折到 30° 楔，再沿角平分线对折得到 15°。二十四层很厚，建议薄纸并在剪前先试压。',
    goodFor: '复杂放射纹样',
    steps: [
      step(1, '按六角放射折折到 30° 楔（十二层）。', 0, 'valley', '需要 30° 辅助线。'),
      step(2, '沿 30° 楔的角平分线对折。', 15, 'valley', '得到 15° 楔，二十四层。'),
    ],
  },
};

export const FOLD_LIST: FoldDefinition[] = Object.values(FOLDS);

export const FOLD_IDS = Object.keys(FOLDS) as FoldId[];

/** 依次包含的关系，用于"切换折法"时提示兼容性。 */
export const FOLD_ORDER: FoldId[] = [
  'half',
  'quarter',
  'radial45',
  'radial36',
  'radial30',
  'radial22_5',
  'radial15',
];

export function getFold(id: FoldId): FoldDefinition {
  const f = FOLDS[id];
  if (!f) throw new Error(`未知折法: ${id}`);
  return f;
}

/** 尺寸语义的中文说明，用于界面与 PDF，禁止不同模式混用而不解释。 */
export function describeSizeKind(kind: SizeKind): string {
  switch (kind) {
    case 'diameter':
      return '外接圆直径';
    case 'widthHeight':
      return '展开后宽 × 高';
    case 'paperEdge':
      return '取纸边长';
  }
}